import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle,
  FileText,
  Download,
  CreditCard,
  Wallet,
} from "lucide-react";
import type { PortalState } from "@/store/portal-store";

interface Step10Props {
  state: PortalState;
}

export function Step10Confirmation({ state }: Step10Props) {
  const quote = state.quote;
  const premium = quote?.monthlyPremium ?? 0;

  return (
    <div className="flex flex-col items-center px-6 py-8">
      {/* Celebration */}
      <div className="relative mb-4">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-in zoom-in">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        {/* Confetti dots */}
        <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full animate-bounce" />
        <div className="absolute -top-2 right-0 w-2 h-2 bg-green-400 rounded-full animate-bounce delay-100" />
        <div className="absolute top-0 -right-4 w-3 h-3 bg-yellow-400 rounded-full animate-bounce delay-200" />
        <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-pink-400 rounded-full animate-bounce delay-300" />
        <div className="absolute bottom-0 right-2 w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-500" />
      </div>

      <h1 className="text-2xl font-bold mb-1">You're Covered!</h1>
      <p className="text-gray-700 font-semibold mb-1">
        Policy #: {state.policyNumber || "ART-20260318"}
      </p>
      <p className="text-gray-500 text-sm mb-6">Effective: March 18, 2026</p>

      {/* Coverage Summary */}
      <Card className="w-full mb-4">
        <CardContent className="pt-4 space-y-3">
          <h3 className="font-semibold">Coverage Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Auto Liability</span>
              <span className="font-medium">${quote?.baseALPremium ?? 780}/mo</span>
            </div>
            {quote?.ntlIncluded && (
              <div className="flex justify-between">
                <span className="text-gray-500">Non-Trucking Liability</span>
                <span className="font-medium">${quote.ntlPrice}/mo</span>
              </div>
            )}
            {quote?.ocacAdded && (
              <div className="flex justify-between">
                <span className="text-gray-500">Occupational Accident</span>
                <span className="font-medium">${quote.ocacPrice}/mo</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">State Fees</span>
              <span className="font-medium">${quote?.stateFees ?? 67}/mo</span>
            </div>
            <div className="flex justify-between border-t pt-2 font-semibold">
              <span>Total Monthly</span>
              <span>${premium}/mo</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card className="w-full mb-4">
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-3">Your Documents</h3>
          <div className="space-y-2">
            {[
              { icon: CreditCard, label: "Insurance ID Cards" },
              { icon: FileText, label: "Full Policy Document" },
              { icon: FileText, label: "Certificate of Insurance (COI)" },
            ].map(({ icon: Icon, label }) => (
              <button
                key={label}
                type="button"
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium flex-1 text-left">
                  {label}
                </span>
                <Download className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card className="w-full mb-4 bg-blue-50 border-blue-100">
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-3">What's Next</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              Your ID cards have been sent to your email and phone
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              {state.bankEnrolled
                ? `Your first ACH payment of $${premium} will draft on April 18, 2026`
                : "Please set up ACH by April 18, 2026"}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              Need to make changes? Contact us at (888) 555-0199 or
              support@artusinsurance.com
            </li>
          </ul>

          {!state.bankEnrolled && (
            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-700 font-medium">
                Important: Set up your bank account for monthly ACH payments to
                avoid policy cancellation.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add to Wallet */}
      <Button variant="outline" className="w-full h-12 gap-2 mb-6">
        <Wallet className="w-5 h-5" />
        Add to Wallet
      </Button>

      <p className="text-xs text-gray-400 text-center">
        Thank you for choosing Artus Insurance. An AgentsHouse MGU Company.
      </p>
    </div>
  );
}
